import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  HiOutlineArrowRight,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineBookOpen,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineMagnifyingGlass,
  HiOutlineUsers,
  HiOutlineClipboardDocumentList,
  HiOutlineCalendarDays,
  HiOutlineCalendar,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCheckBadge,
  HiOutlineWallet,
  HiUser,
  HiOutlineSquares2X2,
  HiOutlineDocumentCurrencyDollar,
  HiOutlineShoppingCart,
  HiOutlineTruck,
  HiOutlineBriefcase,
  HiOutlineSun,
  HiHomeModern,
  HiAcademicCap,
  HiOutlineClipboardDocumentCheck,
} from 'react-icons/hi2';
import { GoArrowRight } from 'react-icons/go';
import { documents, documentsByCategory } from '../data/documents';
import { searchPortalDocuments } from '../data/pageDocuments';
import { RouteLink, navigate, useLocationInfo } from '../route';

const iconMap = {
  HiOutlineUsers,
  HiOutlineClipboardDocumentList,
  HiOutlineCalendarDays,
  HiOutlineCalendar,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCheckBadge,
  HiOutlineWallet,
  HiUser,
  HiOutlineSquares2X2,
  HiOutlineDocumentCurrencyDollar,
  HiOutlineShoppingCart,
  HiOutlineTruck,
  HiOutlineBriefcase,
  HiOutlineSun,
  HiOutlineBookOpen,
  HiHomeModern,
  HiAcademicCap,
  HiOutlineClipboardDocumentCheck,
};

function getIcon(iconName) {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent className="icon" aria-hidden="true" /> : null;
}

function BrandMark() {
  return (
    <img
      className="brand-logo"
      src="/assets/EduX_Logo-removebg-preview.png"
      alt="EduX logo"
    />
  );
}

let savedSidebarScrollTop = 0;
let savedOpenGroups = null;

function getDefaultOpenGroups(pathname) {
  return Object.fromEntries(
    documentsByCategory.map((group, index) => [
      group.category,
      pathname === '/'
        ? index === 0
        : group.items.some((document) => document.route === pathname),
    ])
  );
}

function getInitialOpenGroups(pathname) {
  const nextOpenGroups = savedOpenGroups
    ? { ...savedOpenGroups }
    : getDefaultOpenGroups(pathname);

  const activeGroup = documentsByCategory.find((group) =>
    group.items.some((document) => document.route === pathname)
  );

  if (activeGroup) {
    nextOpenGroups[activeGroup.category] = true;
  }

  return nextOpenGroups;
}

function getInitialOpenSections(document) {
  if (!document?.sections?.length) {
    return {};
  }

  return Object.fromEntries(
    document.sections
      .filter((section) => section.subsections?.length)
      .map((section) => [section.id, true])
  );
}

export default function PortalLayout({ children, currentDocument = null }) {
  const { pathname, search } = useLocationInfo();
  const sidebarRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [openGroups, setOpenGroups] = useState(() =>
    getInitialOpenGroups(window.location.pathname)
  );

  useEffect(() => {
    const params = new URLSearchParams(search);
    const searchQuery = pathname === '/' ? params.get('q') || '' : '';
    setSearchText(searchQuery);
  }, [pathname, search]);

  const normalizedSearch = searchText.trim().toLowerCase();
  const activeDocument = useMemo(
    () => documents.find((document) => document.route === pathname) || null,
    [pathname]
  );
  const activeSidebarDocument = currentDocument || activeDocument;
  const [openSections, setOpenSections] = useState(() =>
    getInitialOpenSections(currentDocument || activeDocument)
  );

  useEffect(() => {
    savedOpenGroups = openGroups;
  }, [openGroups]);

  useEffect(() => {
    setOpenSections(getInitialOpenSections(activeSidebarDocument));
  }, [activeSidebarDocument]);

  useLayoutEffect(() => {
    const sidebar = sidebarRef.current;

    if (!sidebar) {
      return undefined;
    }

    sidebar.scrollTop = savedSidebarScrollTop;
  }, []);

  useEffect(() => {
    const sidebar = sidebarRef.current;

    if (!sidebar) {
      return undefined;
    }

    function handleSidebarScroll() {
      savedSidebarScrollTop = sidebar.scrollTop;
    }

    sidebar.addEventListener('scroll', handleSidebarScroll);

    return () => {
      savedSidebarScrollTop = sidebar.scrollTop;
      sidebar.removeEventListener('scroll', handleSidebarScroll);
    };
  }, []);

  const searchResults = useMemo(() => {
    if (!normalizedSearch) {
      return [];
    }

    return searchPortalDocuments(normalizedSearch).slice(0, 6);
  }, [normalizedSearch]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();

    const value = searchText.trim();
    navigate(value ? `/?q=${encodeURIComponent(value)}` : '/');
  };

  const toggleGroup = (category) => {
    setOpenGroups((current) => ({
      ...current,
      [category]: !current[category],
    }));
  };

  const toggleSection = (sectionId) => {
    setOpenSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }));
  };

  return (
    <div className="portal-shell">
      <header className="portal-topbar">

        <div className="brand-lockup">
          <BrandMark />

          <div className="brand-copy">
            <span className="brand-eyebrow"> ERP</span>
            <div className="brand-heading">
              <RouteLink className="brand-title" to="/">
                Documentation
              </RouteLink>
            </div>
          </div>

        </div>

         

        <form className="topbar-search-shell" role="search" onSubmit={handleSearchSubmit}>
          <span className="topbar-search-icon" aria-hidden="true">
            <HiOutlineMagnifyingGlass className="icon" />
          </span>
          <input
            aria-label="Search modules"
            type="search"
            placeholder="Search modules, steps, keywords, or categories"
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
          />
          <button type="submit" className="topbar-search-button">
            <span>Search</span>
            <HiOutlineArrowRight className="icon" aria-hidden="true" />
          </button>

          {normalizedSearch ? (
            <div className="search-results" role="listbox" aria-label="Search suggestions">
              {searchResults.length ? (
                searchResults.map((document) => (
                  <RouteLink
                    className="search-result-item"
                    key={`${document.slug}-${document.matchLabel}`}
                    to={document.matchRoute || document.route}
                  >
                    <div className="search-result-copy">
                      <span className="search-result-title">{document.title}</span>
                      <span className="search-result-meta">
                        {document.category} · {document.matchLabel}
                      </span>
                      <span className="search-result-snippet">{document.matchSnippet}</span>
                    </div>
                    <HiOutlineArrowRight className="icon" aria-hidden="true" />
                  </RouteLink>
                ))
              ) : (
                <div className="search-empty-state">
                  <strong>No matching modules</strong>
                  <span>Try terms like admission, payroll, attendance, or records.</span>
                </div>
              )}
            </div>
          ) : null}
        </form>

        <div className="topbar-actions">
          <a className="topbar-cta" href="https://eduxerp.com/">
            <span>Try Free</span>
            <HiOutlineArrowTopRightOnSquare className="icon" aria-hidden="true" />
          </a>
        </div>
      </header>

      <div className="portal-body">
        <aside className="portal-sidebar" ref={sidebarRef}>
          <div className="sidebar-panel">
            <div className="sidebar-panel-title">
              <HiOutlineBookOpen className="sidebar-title-icon" aria-hidden="true" />
              <h2>User Docs</h2>
            </div>
          </div>

          {documentsByCategory.map((group) => {
            const groupIsActive = group.items.some((document) => document.route === pathname);
            const isOpen = openGroups[group.category];

            return (
              <section
                className={groupIsActive ? 'sidebar-group active' : 'sidebar-group'}
                key={group.category}
              >
                <button
                  type="button"
                  className="sidebar-group-toggle"
                  onClick={() => toggleGroup(group.category)}
                  aria-expanded={isOpen ? 'true' : 'false'}
                >
                  <div className="sidebar-group-header">
                    <div className="sidebar-group-title">
                      {isOpen ? (
                        <HiOutlineChevronDown className="icon chevron-icon" aria-hidden="true" />
                      ) : (
                        <HiOutlineChevronRight className="icon chevron-icon" aria-hidden="true" />
                      )}
                      <h3>{group.category}</h3>
                    </div>
                    <span>{group.items.length}</span>
                  </div>
                </button>

                {isOpen ? (
                  <div className="sidebar-links">
                    {group.items.map((document) => {
                      const isActiveDocument = pathname === document.route;

                      return (
                        <div className="sidebar-link-stack" key={document.slug}>
                          <RouteLink
                            className={isActiveDocument ? 'sidebar-link active' : 'sidebar-link'}
                            to={document.route}
                          >
                            <div className="sidebar-link-content">
                              {document.icon ? (
                                <div className="sidebar-link-icon">{getIcon(document.icon)}</div>
                              ) : null}
                              <span className="sidebar-link-label">{document.title}</span>
                            </div>
                          </RouteLink>

                          {isActiveDocument && activeSidebarDocument ? (
                            <div className="sidebar-section-tree">
                              {activeSidebarDocument.sections.map((section) => {
                                const hasSubsections = Boolean(section.subsections?.length);
                                const isSectionOpen = hasSubsections
                                  ? openSections[section.id] !== false
                                  : false;

                                return (
                                  <div className="sidebar-section-group" key={section.id}>
                                    <div className="sidebar-section-row">
                                      {hasSubsections ? (
                                        <button
                                          type="button"
                                          className="sidebar-section-toggle"
                                          onClick={() => toggleSection(section.id)}
                                          aria-expanded={isSectionOpen ? 'true' : 'false'}
                                          aria-label={
                                            isSectionOpen
                                              ? `Collapse ${section.title}`
                                              : `Expand ${section.title}`
                                          }
                                        >
                                          {isSectionOpen ? (
                                            <HiOutlineChevronDown
                                              className="icon"
                                              aria-hidden="true"
                                            />
                                          ) : (
                                            <HiOutlineChevronRight
                                              className="icon"
                                              aria-hidden="true"
                                            />
                                          )}
                                        </button>
                                      ) : (
                                        <span className="sidebar-section-marker" aria-hidden="true">
                                          <HiOutlineChevronRight className="icon" />
                                        </span>
                                      )}
                                      <RouteLink
                                        className="sidebar-section-link"
                                        to={`${document.route}#${section.id}`}
                                      >
                                        {section.title}
                                      </RouteLink>
                                    </div>

                                    {hasSubsections && isSectionOpen ? (
                                      <div className="sidebar-subsection-list">
                                        {section.subsections.map((subsection) => (
                                          <RouteLink
                                            className="sidebar-subsection-link"
                                            to={`${document.route}#${subsection.id}`}
                                            key={subsection.id}
                                          >
                                            <span
                                              className="sidebar-subsection-marker"
                                              aria-hidden="true"
                                            >
                                              <GoArrowRight />
                                            </span>
                                            <span>{subsection.title}</span>
                                          </RouteLink>
                                        ))}
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </section>
            );
          })}
        </aside>

        <main className="portal-main">{children}</main>
      </div>
    </div>
  );
}
